import { Component } from 'react'
import {
    Button,
    Col,
    CustomInput,
    FormFeedback,
    FormGroup,
    Label,
    ModalBody,
    ModalFooter,
    Row,
} from 'reactstrap'
import PropTypes from 'prop-types'

import { PacoLogsModal, WaitingDayHoursInput } from '../../../@paco/compositions'
import { transformLegacyRoleToRole } from '../../../@paco/entities/Role/RoleTransformers'
import checkPermission from '../../../@paco/helpers/permissions/checkPermission'

import FormDatePicker from '../../../components/FormDatePicker/FormDatePicker'
import ModalHeader from '../../../components/ModelHeader/ModalHeader'
import UserProfileInfo from '../../../components/UserProfileInfo/UserProfileInfo'
import WeekdayHoursInputs from '../../../components/WeekHoursInputs/WeekHoursInputs'
import { getObjProperty, getPermissionToDeleteAbsenceHours, getUserFullName } from '../../../helpers'
import { compareAsc, formatDate, max } from '../../../helpers/date'
import UpToAndIncludingDate from '../../../helpers/date/UpToAndIncludingDate'
import { getPermissionToAdd, getPermissionToEdit } from '../../../helpers/permissions/getPermissionToAction'
import { translate } from '../../../helpers/translations/translator'
import transformAbsenceHoursToWaitingDayHours from '../../../services/AbsenceHoursService/transformAbsenceHoursToWaitingDayHours'
import getAbsencesForEmployeeInLast4Weeks from '../helpers/getAbsencesForEmployeeInLast4Weeks'
import { isAllWeeksLocked } from './EditAbsenceForm/helpers/isAllWeeksLocked'
import { transformAbsenceHoursToWeekDayWithHours } from './EditAbsenceForm/helpers/transformAbsenceHoursToWeekDayWithHours'

import './Forms.scss'


class ResolveAbsenceForm extends Component {
    state = {
        endDate: null,
        endTime: '',
        canEditAbsencesHours: false,
        canSubmitDate: true,
        canSubmitTime: true,
        canSubmit: true,
        canViewLogs: true,
        showHistory: false,
        waitingDayHours: null,
        userHasWaitingDayHours: false,
        userHasWaitingDayHoursLoading: false,
    }

    componentDidMount() {
        const start = getObjProperty(this.props, 'absence.start')
        this.doesUserHaveWaitingDayHours()
        this.setState({
            endDate: formatDate(max([new Date(), start]), 'yyyy-MM-dd'),
            endTime: formatDate(new Date(), 'HH:mm:00'),
            waitingDayHours: transformAbsenceHoursToWaitingDayHours(this.props.absence.absenceHours),
        })
    }

    static getDerivedStateFromProps(props, state) {
        const { absence, currentUserRole, permissions } = props
        const { endDate, endTime } = state
        const compareDatesResult = compareAsc(endDate, formatDate(absence.start, 'yyyy-MM-dd'))
        const canSubmitDate = compareDatesResult >= 0
        const canSubmitTime = compareDatesResult !== 0 ? true : endTime >= formatDate(absence.start, 'HH:mm:ss')
        const canSubmit = canSubmitDate && canSubmitTime && endTime && endTime.length
        const absenceUserRoles = absence.user.roles.map(transformLegacyRoleToRole)
        const canAddAbsencesHours = getPermissionToAdd(currentUserRole, absenceUserRoles, permissions, 'absence-hours')
        const canEditAbsencesHours = getPermissionToEdit(currentUserRole, absenceUserRoles, permissions, 'absence-hours')
        const canDeleteAbsencesHours = getPermissionToDeleteAbsenceHours(currentUserRole, permissions)
        const canViewLogs = checkPermission(permissions, 'view-all-logs')

        return {
            ...state,
            canEditAbsencesHours: canEditAbsencesHours && canAddAbsencesHours && canDeleteAbsencesHours,
            canSubmitDate,
            canSubmitTime,
            canSubmit,
            canViewLogs,
        }
    }

    doesUserHaveWaitingDayHours = async () => {
        const { absence } = this.props
        const { user, start } = absence

        this.setState({
            userHasWaitingDayHoursLoading: true,
        })

        const absences = await getAbsencesForEmployeeInLast4Weeks(user.id, start, absence.id)

        this.setState({
            userHasWaitingDayHoursLoading: false,
            userHasWaitingDayHours: ! absences.length,
        })
    }

    onFormSubmit = (e) => {
        e.preventDefault()
        const { absence } = this.props
        const {
            canEditAbsencesHours,
            endDate,
            endTime,
            weeksWithHours,
            waitingDayHours,
        } = this.state

        this.props.onSubmit(
            absence,
            {
                start: absence.start,
                end: `${endDate} ${endTime}`,
                waitingDayHours,
            },
            canEditAbsencesHours ? weeksWithHours : [],
        )
    }

    onChangeDate = (val) => {
        this.setState({
            endDate: val ? formatDate(val, 'yyyy-MM-dd') : null,
        })
    }

    onChangeTime = (val) => {
        this.setState({
            endTime: val.target.value,
        })
    }

    onChangeWaitingDayHours = (value) => {
        this.setState({
            waitingDayHours: value === undefined ? null : value,
        })
    }

    onWeekdayHoursInputsChange = (weeksWithHours) => {
        this.setState({
            weeksWithHours,
        })
    }

    dispatchOpenLogsModal = (id) => {
        this.props.onShowLogsClick(id)

        this.setState({
            showHistory: true,
        })
    }

    dispatchCloseLogsModal = () => {
        this.setState({ showHistory: false })
    }

    render() {
        const {
            endDate,
            endTime,
            canEditAbsencesHours,
            canSubmitDate,
            canSubmitTime,
            canSubmit,
            canViewLogs,
            waitingDayHours,
            userHasWaitingDayHours,
            userHasWaitingDayHoursLoading,
        } = this.state
        const {
            onCancel,
            payrollPeriods,
            pacoPayrollPeriods,
            absence = {},
        } = this.props
        const { user } = absence
        const payrollPeriodsEndDate = (endDate && endTime) ? `${endDate} ${endTime}` : null
        const weekWithHours = transformAbsenceHoursToWeekDayWithHours(
            absence.absenceHours || [],
            pacoPayrollPeriods,
        )
        const formattedStartDate = formatDate(absence.start, 'dd-MM-yyyy')
        const formattedStartTime = formatDate(absence.start, 'HH:mm')

        const isPeriodLocked = isAllWeeksLocked(this.state.weeksWithHours, absence.start)

        return (
            <form ref={this.formRef} onSubmit={this.onFormSubmit}>
                <ModalHeader
                    showHistoryButton={canViewLogs}
                    title={translate('pages.absences.resolveAbsenceFrom')}
                    isLoading={userHasWaitingDayHoursLoading}
                    onHistoryButtonClick={() => this.dispatchOpenLogsModal(absence.id)}
                />
                <ModalBody>
                    { user && (
                        <Row>
                            <Col>
                                <FormGroup>
                                    <UserProfileInfo
                                        date={new Date(absence.start)}
                                        experience={user?.experience?.name}
                                        name={getUserFullName(user)}
                                        userId={user.id}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    )}
                    <Row>
                        <Col>
                            <FormGroup>
                                <Label>{translate('pages.tracks.startDate')}</Label>
                                <span className="form-control form-absence__start-date">{formattedStartDate}</span>
                            </FormGroup>
                        </Col>

                        <Col>
                            <FormGroup>
                                <Label>{translate('pages.tracks.startTime')}</Label>
                                <span className="form-control form-absence__start-time">{formattedStartTime}</span>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <FormGroup>
                                <Label>{translate('common.endDate')}</Label>
                                <FormDatePicker
                                    invalid={! canSubmitDate}
                                    selected={endDate}
                                    onChange={val => this.onChangeDate(val)}
                                    feedback={translate('pages.management.endDateNeedsToBeLaterThanStartDate')}
                                />
                            </FormGroup>
                        </Col>
                        <Col>
                            <FormGroup>
                                <FormGroup>
                                    <Label>{translate('pages.tracks.endTime')}</Label>
                                    <CustomInput
                                        invalid={! canSubmitTime}
                                        type="time"
                                        className="form-control"
                                        id="end-time"
                                        name="endTime"
                                        onChange={val => this.onChangeTime(val)}
                                        defaultValue={this.state.endTime}
                                    />
                                    <FormFeedback>
                                        {translate('pages.management.endTimeNeedsToBeLaterOrEqualToStartTime')}
                                    </FormFeedback>
                                </FormGroup>
                            </FormGroup>
                        </Col>
                    </Row>
                    { userHasWaitingDayHours && (
                        <Row>
                            <Col>
                                <WaitingDayHoursInput
                                    isDisabled={! canEditAbsencesHours || isPeriodLocked}
                                    value={waitingDayHours === null ? undefined : waitingDayHours}
                                    onChange={this.onChangeWaitingDayHours}
                                />
                            </Col>
                            <Col />
                        </Row>
                    )}
                    <WeekdayHoursInputs
                        disabled={! canEditAbsencesHours}
                        payrollPeriods={payrollPeriods}
                        pacoPayrollPeriods={pacoPayrollPeriods}
                        weekWithHours={weekWithHours}
                        type="absences"
                        startDate={new Date(absence.start)}
                        endDate={new UpToAndIncludingDate(payrollPeriodsEndDate)}
                        onChange={this.onWeekdayHoursInputsChange}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button type="button" color="link" id="modal-close" onClick={onCancel}>
                        {translate('common.cancel')}
                    </Button>
                    <Button type="submit" color="orange" disabled={! canSubmit}>{translate('common.save')}</Button>
                </ModalFooter>

                <PacoLogsModal
                    isLoading={this.props.logsLoading}
                    isOpen={this.state.showHistory && ! this.props.logsLoading}
                    title={translate('pages.absences.absenceLog')}
                    emptyMessage={translate('pages.absences.emptyAbsenceLog')}
                    logs={this.props.logs}
                    onClose={this.dispatchCloseLogsModal}
                />
            </form>
        )
    }
}

ResolveAbsenceForm.propTypes = {
    absence: PropTypes.object,
    logs: PropTypes.array,
    logsLoading: PropTypes.bool.isRequired,
    currentUserRole: PropTypes.object.isRequired,
    payrollPeriods: PropTypes.array.isRequired,
    pacoPayrollPeriods: PropTypes.array.isRequired,
    permissions: PropTypes.array.isRequired,
    onCancel: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onShowLogsClick: PropTypes.func.isRequired,
}

export default ResolveAbsenceForm
